import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { rem } from "polished";
import React, { useEffect, useState } from "react";
import { useMeasure } from "react-use";
import { useDrag } from "react-use-gesture";

import { clamp } from "../../lib/math";

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  min-width: ${rem(350)};
  flex: 1;

  height: 2.8rem;

  display: flex;
  align-items: center;
`;

const SliderTrackOuter = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  flex: 1;

  height: 1rem;
  border-radius: 5px;
  border: solid 1px rgba(0, 0, 0, 0.1);
  background-color: #fafafa;

  display: flex;
  align-items: center;
`;

const SliderTrackInner = styled.div`
  position: relative;
  z-index: 1;

  height: 100%;
  border-radius: 5px;
`;

interface SliderTrackProps {
  x: number;
  color: string;
  offset?: number;
}

const SliderTrack = ({ x, offset = 0, color }: SliderTrackProps) => (
  <SliderTrackOuter>
    <SliderTrackInner style={{ width: x + offset, backgroundColor: color }} />
  </SliderTrackOuter>
);

const SliderHandle = styled.div<{ x: any }>`
  position: absolute;
  z-index: 2;

  width: ${rem(42)};
  height: ${rem(42)};
  border-radius: ${rem(42)};
  box-shadow: 0 0.1rem 0.8rem 0 rgba(0, 0, 0, 0.1);
  border: solid 0.1rem rgba(51, 51, 51, 0.2);
  background-color: #ffffff;

  cursor: pointer;
  user-select: none;

  transition: transform 0.1s;
  transform-style: preserve-3d;
  transform-origin: center;

  left: ${(props) => props.x}px;

  &:hover {
    transform: scale3d(1.04, 1.04, 1);
    /* Implement on mousedown */
    background-color: #fafafa;
  }
`;

interface SliderProps {
  draggable?: boolean;
  disabled?: boolean;
  color?: string;
  position: number | string;
  maxPosition?: number | string;
  onDrag?: (x: number) => void;
}

const useWidth = (initial: number): [(instance: unknown) => void, number] => {
  const [ref, { width }] = useMeasure();

  return [ref, width || initial];
};

const Slider = ({
  disabled = false,
  draggable = false,
  color,
  position,
  maxPosition,
  onDrag = () => {},
}: SliderProps) => {
  const [ref, width] = useWidth(350);
  const extra = width / 14.2857142857;
  const endX = width - (draggable ? extra : 0);

  const maxX =
    typeof maxPosition === "undefined" ? endX : Number(maxPosition) * endX;

  const x = Math.min(Number(position) * endX, maxX);

  const [dragging, setDragging] = useState(false);
  const [lastX, setLastX] = useState(x);
  const [dragX, setDragX] = useState(x);

  const bind = useDrag(({ down, first, last, movement }) => {
    const newX = clamp(lastX + movement[0], 0, maxX);
    if (first) {
      setDragging(true);
    }
    if (down) {
      setDragX(newX);
    }
    if (last) {
      setDragging(false);
      setLastX(newX);
    }
  });

  // const updatedPos = dragX / maxX
  const updatedPos = dragX / endX;

  useEffect(
    () => {
      if (!dragging) {
        setDragX(x);
        setLastX(x);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME Why isn't `dragging` in the deps?
    [position, x]
  );

  useEffect(
    () => {
      if (dragging) {
        onDrag(updatedPos);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME Why isn't `onDrag` in the deps?
    [dragging, updatedPos]
  );

  return (
    <>
      <SliderContainer
        ref={ref}
        css={
          disabled
            ? css`
                opacity: 0.5;
              `
            : null
        }
      >
        <SliderTrack
          color={color}
          offset={draggable ? extra / 2 : 0}
          x={dragX}
        />
        {draggable ? (
          <SliderHandle {...(!disabled && bind())} x={dragX} />
        ) : null}
      </SliderContainer>
    </>
  );
};

export default Slider;
